import { withFallbackQuickReplies } from '@/shared/constants/quick-replies';
import {
	ICreateDraftFormResponse,
	ICreateDuplicateFormResponse,
	IForm,
	IFormCounts,
	IFormCustomField,
	IFormEditDetailsResponse,
	IFormFilledByUserResponse,
	IFormInputField,
	IFormUser,
	IGetFormFillingDetailsResponse,
	IGetFormUsersResponse,
	ILabelWithColor,
	IPageDetails,
	ISCreateDraftFormResponse,
	ISCreateDuplicateFormResponse,
	ISForm,
	ISFormAiField,
	ISFormCounts,
	ISFormCustomField,
	ISFormEditDetailsResponse,
	ISFormFilledByUserResponse,
	ISFormInputField,
	ISFormUser,
	ISGetFormFillingDetailsResponse,
	ISGetFormUsersResponse,
	ISLabelWithColor,
	ISUserFormsResponse,
	IUpdateFormEditDetailsPayload,
	IUserFormsResponse,
} from '@/shared/services/forms/forms.types';

import { getStructuredPageDetails } from '../common';

export function getStructuredFormCounts(obj: IFormCounts): ISFormCounts {
	return {
		respondedCount: obj.responded_count ?? 0,
		totalCount: obj.total_count ?? 0,
		statusCounts: obj.status_counts ?? [],
	};
}

export function getFormCountsPayload(obj: ISFormCounts): IFormCounts {
	return {
		responded_count: obj.respondedCount,
		total_count: obj.totalCount,
		status_counts: obj.statusCounts,
	};
}

export function getStructuredInputField(obj: IFormInputField): ISFormInputField {
	const updatedObj: ISFormInputField = {
		id: obj.id,
		question: obj.question,
		required: obj.required,
		type: obj.type,
		visibility: obj.visibility ?? true,
		tableOrder: obj.table_order ?? -1,
		width: obj.width,
		allowOther: obj.allow_other ?? false,
	};

	if (obj.description) {
		updatedObj.description = obj.description;
	}

	if (obj.pre_defined_input_type) {
		updatedObj.preDefinedInputType = obj.pre_defined_input_type;
	}

	if (obj.options) {
		updatedObj.options = obj.options;
	}

	if (obj.file_types) {
		updatedObj.fileTypes = obj.file_types;
	}

	if (typeof obj.valid === 'boolean') {
		updatedObj.valid = obj.valid;
	}

	if (obj.draft_presigned_url) {
		updatedObj.draftPresignedUrl = obj.draft_presigned_url;
	}

	if (typeof obj.is_protected === 'boolean') {
		updatedObj.isProtected = obj.is_protected;
	}

	return updatedObj;
}

export function getStructuredCustomField(obj: IFormCustomField): ISFormCustomField {
	const updatedObj: ISFormCustomField = {
		id: obj.id,
		type: obj.type,
		question: obj.question,
		required: obj.required,
		valid: obj.valid,
		visibility: obj.visibility ?? true,
		tableOrder: obj.table_order ?? -1,
		width: obj.width,
		isProtected: obj.is_protected,
	};

	return updatedObj;
}

export function getStructuredInputFieldForForm(obj: IFormInputField): ISFormInputField {
	return {
		...getStructuredInputField(obj),
		type: obj.type,
	};
}

export function getInputFieldPayload(obj: ISFormInputField): IFormInputField {
	const updatedObj: IFormInputField = {
		id: obj.id,
		question: obj.question?.trim() ?? '',
		required: obj.required,
		type: obj.type,
	};

	if (obj.description) {
		updatedObj.description = obj.description;
	}

	if (obj.preDefinedInputType) {
		updatedObj.pre_defined_input_type = obj.preDefinedInputType;
	}

	if (obj.options) {
		updatedObj.options = obj.options;
	}

	if (obj.fileTypes) {
		updatedObj.file_types = obj.fileTypes;
	}

	if (typeof obj.valid === 'boolean') {
		updatedObj.valid = obj.valid;
	}

	if (typeof obj.allowOther === 'boolean') {
		updatedObj.allow_other = obj.allowOther;
	}

	return updatedObj;
}

export function getStructuredLabelWithColor(obj: ILabelWithColor): ISLabelWithColor {
	return {
		value: obj.value,
		colour: obj.colour,
	};
}

export function getStructuredForm(obj: IForm): ISForm {
	return {
		identifier: obj.identifier,
		aiNudgeShown: obj.ai_nudge_shown,
		name: obj.name,
		metaImage: obj.meta_image,
		multipleSubmissions: obj.multiple_submissions,
		emailVerificationRequired: obj.email_verification_required,
		getVerifiedApplicantsOnly: obj.email_verification_required,
		status: obj.status,
		stages: obj.stages?.map((stage) => ({
			label: stage.label,
			notificationsEnabled: stage.notifications_enabled,
		})),
		formCounts: {
			respondedCount: obj.form_counts?.responded_count ?? obj.counts?.responded_count ?? 0,
			totalCount: obj.form_counts?.total_count ?? obj.counts?.total_count ?? 0,
			statusCounts: obj.form_counts?.status_counts ?? obj.counts?.status_counts ?? [],
		},
		lastUpdatedTimestamp: obj.last_updated_timestamp ?? '',
		formToken: obj.form_token ?? '',
		pageHandle: obj.page_handle ?? '',
		inputFields:
			obj.input_fields?.map((inputField) => getStructuredInputFieldForForm(inputField)) ?? [],
		labels: obj.labels ?? [],
		labelsWithColor: obj.labels_wc?.map((label) => getStructuredLabelWithColor(label)) ?? [],
		quickReplies: withFallbackQuickReplies(obj.quick_replies),
		defaultArchiveChatStatus: obj.default_archive,
		closingDate: obj.closing_date,
		includedWidgets: obj.included_widgets ?? [],
		fastMode: obj.fast_mode ?? 'DEFAULT',
		savedFilter: obj.saved_filter ?? null,
		formSuccessMessage: obj.form_success_message,
	};
}

export function getStructuredForms(obj: IForm[]): ISForm[] {
	return obj?.map((form) => getStructuredForm(form)) ?? [];
}

export function getStructuredUserFormsResponse(
	obj: IUserFormsResponse,
	payloadPageDetails: IPageDetails
): ISUserFormsResponse {
	return {
		forms: getStructuredForms(obj.forms),
		pageDetails: getStructuredPageDetails({
			...payloadPageDetails,
			...obj.page_details,
		}),
		hasPublishedForms: obj.has_published_forms,
	};
}

export function getStructuredCreateDraftFormResponse(
	obj: ICreateDraftFormResponse
): ISCreateDraftFormResponse {
	return {
		formId: obj.form_id,
	};
}

export function getStructuredFormEditDetailsResponse(
	obj: IFormEditDetailsResponse
): ISFormEditDetailsResponse {
	return {
		id: obj.id,
		name: obj.name,
		description: obj.description,
		status: obj.status,
		formCloseMessage: obj.form_close_message,
		formSuccessMessage: obj.form_success_message,
		includedWidgets: obj.included_widgets,
		createdTimestamp: obj.created_timestamp,
		publishedTimestamp: obj.published_timestamp,
		lastUpdatedTimestamp: obj.last_updated_timestamp,
		isCreator: obj.is_creator,
		inputFields: obj.input_fields?.map((inputField) => getStructuredInputField(inputField)) ?? [],
		multipleSubmissions: obj.multiple_submissions,
		emailVerificationRequired: obj.email_verification_required,
		pageHandle: obj.page_handle,
		pageId: obj.page_id,
		linkedinEnabled: obj.linkedin_enabled,
		linkedinExcludedReason: obj.linkedin_excluded_reason,
		metaImage: obj.meta_image,
		closingDate: obj.closing_date,
		counts: obj.counts ? getStructuredFormCounts(obj.counts) : undefined,
		unarchivedReplyNotif: obj.unarchived_reply_notif,
		showOnPublicPage: obj.public_page_visibility,
		logo: obj.logo,
		templateId: parseInt(obj.template_id + '') || undefined,
		templateColor: obj.template_color,
		locations: obj.locations,
	};
}

export function getFormEditDetailsPayload(
	obj: ISFormEditDetailsResponse
): IUpdateFormEditDetailsPayload {
	return {
		id: obj.id,
		name: obj.name,
		description: obj.description,
		input_fields: obj.inputFields?.map((inputField) => getInputFieldPayload(inputField)) ?? [],
		multiple_submissions: obj.multipleSubmissions,
		email_verification_required: obj.emailVerificationRequired,
		status: obj.status,
		form_close_message: obj.formCloseMessage,
		form_success_message: obj.formSuccessMessage,
		included_widgets: obj.includedWidgets,
		last_updated_timestamp: obj.lastUpdatedTimestamp,
		is_creator: obj.isCreator,
		linkedin_enabled: obj.linkedinEnabled,
		page_handle: obj.pageHandle,
		meta_image: obj.metaImage,
		closing_date: obj.closingDate,
		counts: obj.counts ? getFormCountsPayload(obj.counts) : undefined,
		public_page_visibility: obj.showOnPublicPage,
		template_id: obj.templateId,
		template_color: obj.templateColor,
		locations: obj.locations,
		logo: obj.logo,
	};
}

export function getStructuredGetFormFillingDetailsResponse(
	obj: IGetFormFillingDetailsResponse
): ISGetFormFillingDetailsResponse {
	return {
		...getStructuredFormEditDetailsResponse(obj),
	};
}

export function getStructuredFormFilledByUserResponse(
	obj: IFormFilledByUserResponse
): ISFormFilledByUserResponse {
	return {
		formId: obj.form_id,
		alreadyFilled: obj.already_filled,
		previousSubmissionId: obj.previous_submission_id,
	};
}

export function getStructuredCreateDuplicateFormResponse(
	obj: ICreateDuplicateFormResponse
): ISCreateDuplicateFormResponse {
	return {
		formId: obj.form_id,
	};
}

export function getStructuredFormUser(obj: IFormUser): ISFormUser {
	return {
		userId: obj.user_id,
		email: obj.email,
		role: obj.role,
		image: obj.image,
		name: obj.name,
		lastActivity: obj.last_activity,
		pageRole: obj.page_role,
	};
}

export function getStructuredGetFormUsersResponse(
	obj: IGetFormUsersResponse
): ISGetFormUsersResponse {
	return {
		users: obj.users?.map((user) => getStructuredFormUser(user)) ?? [],
	};
}

export type AnyFormField = ISFormInputField | ISFormAiField | ISFormCustomField;

export function isFieldProtected(field: AnyFormField): boolean {
	return 'isProtected' in field && !!field.isProtected;
}
